// components/layout/Header.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  ShoppingCart,
  User,
  LogOut,
  Moon,
  Sun,
  ChevronDown,
  Bell,
  Search,
  Sparkles,
  Crown,
  Shield,
  CreditCard,
  Settings,
  Wallet,
  ShoppingBag,
  ClipboardList,
  Home,
  Store,
  TrendingUp,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useTheme } from "../../hooks/useTheme";
import { useToast } from "../../providers/ToastProvider";
import CommandPalette from "../CommandPalette";
import azoemartLogo from "../../assets/images/azoemart.png";
import { useCart } from "../../contexts/CartContext";

const QUICK_NAV = [
  { path: "/", label: "Home", icon: Home },
  { path: "/data", label: "Data", icon: TrendingUp },
  { path: "/marketplace", label: "Shop", icon: Store },
];

const ROLE_META = {
  ADMIN: { label: "Admin", icon: Shield, tone: "error" },
  AGENT: { label: "Agent", icon: Crown, tone: "warning" },
  SUB_AGENT: { label: "Sub-agent", icon: Sparkles, tone: "link" },
  CUSTOMER: { label: "Customer", icon: User, tone: "success" },
};

const getRoleTone = (colors, tone) => {
  const map = {
    error: colors.error,
    warning: colors.warning,
    link: colors.linkColor,
    success: colors.success,
  };
  return map[tone] || colors.secondaryText;
};

const initialsOf = (user) => {
  if (!user) return "?";
  const first = user.first_name?.[0] || "";
  const last = user.last_name?.[0] || "";
  if (first || last) return (first + last).toUpperCase();
  const src = user.username || user.phone_number || "?";
  return src.slice(0, 2).toUpperCase();
};

const Header = ({ toggleSidebar, isMobile, sidebarOpen = false }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const { colors, theme, updateTheme } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [showDropdown, setShowDropdown] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const dropdownRef = useRef(null);
  const avatarBtnRef = useRef(null);
  const { count: cartCount } = useCart();

  // ── Outside click / Escape ─────────────────────────────
  useEffect(() => {
    const onDown = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !avatarBtnRef.current?.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // ── ⌘K / Ctrl+K opens the palette ─────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
      if (e.key === "Escape") setShowDropdown(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      showToast("Logged out", "success");
      navigate("/login");
    } catch {
      showToast("Logout failed", "error");
    }
    setShowDropdown(false);
  }, [logout, navigate, showToast]);

  const toggleTheme = useCallback(() => {
    updateTheme({ mode: theme.mode === "dark" ? "light" : "dark", system: false });
  }, [theme.mode, updateTheme]);

  const displayName = useMemo(
    () =>
      user?.full_name || user?.username || user?.phone_number || "Guest",
    [user]
  );

  const roleMeta = ROLE_META[user?.role] || null;
  const roleTone = roleMeta ? getRoleTone(colors, roleMeta.tone) : null;

  // On desktop the sidebar already shows the brand, so the header hides it
  // to avoid duplicate logos. On mobile the sidebar is a drawer, so the
  // header keeps the brand visible at all times.
  const showBrand = isMobile || !sidebarOpen;

  return (
    <>
      <header style={styles.header(colors)}>
        <div style={styles.rail(isMobile)}>
          {/* ── LEFT ──────────────────────────────────── */}
          <div style={styles.leftCluster}>
            <IconButton
              colors={colors}
              onClick={toggleSidebar}
              aria-label="Toggle menu"
            >
              <Menu size={isMobile ? 20 : 19} />
            </IconButton>

            {showBrand && (
              <button
                type="button"
                onClick={() => navigate("/")}
                style={styles.brandRow}
                aria-label="Azoemart home"
              >
                <img
                  src={azoemartLogo}
                  alt="Azoemart"
                  style={styles.brandLogo(isMobile)}
                />
                {!isMobile && (
                  <span style={styles.brandText(colors)}>
                    Azoe<span style={{ color: colors.btnBg }}>mart</span>
                  </span>
                )}
              </button>
            )}
          </div>

          {/* ── CENTER: quick nav (desktop only) ─────── */}
          {!isMobile && (
            <nav style={styles.navRow}>
              {QUICK_NAV.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => navigate(item.path)}
                    style={styles.navLink(colors)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = colors.hoverBg;
                      e.currentTarget.style.color = colors.text;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = colors.secondaryText;
                    }}
                  >
                    <Icon size={14} />
                    {item.label}
                  </button>
                );
              })}
            </nav>
          )}

          {/* ── RIGHT ─────────────────────────────────── */}
          <div style={styles.rightCluster}>
            {/* Search trigger */}
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              aria-label="Search"
              style={styles.searchTrigger(colors, isMobile)}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = colors.btnBg;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = colors.border;
              }}
            >
              <Search size={14} />
              {!isMobile && (
                <>
                  <span style={styles.searchTriggerText(colors)}>
                    Search
                  </span>
                  <kbd style={styles.kbd(colors)}>⌘K</kbd>
                </>
              )}
            </button>

            {/* Wallet (signed in, desktop) */}
            {isAuthenticated && !isMobile && (
              <button
                type="button"
                onClick={() => navigate("/wallet")}
                title="Wallet"
                style={styles.walletPill(colors)}
              >
                <Wallet size={14} color={colors.btnBg} />
                <span>GH₵ 0.00</span>
              </button>
            )}

            {/* Notifications */}
            <IconButton
              colors={colors}
              onClick={() => showToast("Notifications coming soon", "info")}
              aria-label="Notifications"
            >
              <Bell size={isMobile ? 19 : 18} />
              <span
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: colors.error,
                  border: `1.5px solid ${colors.raisedBg}`,
                }}
              />
            </IconButton>

            {/* Theme */}
            <IconButton
              colors={colors}
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              {theme.mode === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </IconButton>

            {/* Cart */}
            <IconButton
              colors={colors}
              onClick={() => navigate("/cart")}
              aria-label="Cart"
            >
              <ShoppingCart size={isMobile ? 19 : 18} />
              {isAuthenticated && (
                <span style={styles.cartBadge(colors)}>
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </IconButton>

            {/* Avatar */}
            <button
              ref={avatarBtnRef}
              type="button"
              onClick={() => setShowDropdown((v) => !v)}
              aria-label="Account menu"
              style={styles.avatarBtn(colors, showDropdown)}
            >
              {isAuthenticated ? (
                <span style={styles.avatarInitials(colors)}>
                  {initialsOf(user)}
                </span>
              ) : (
                <span style={styles.avatarInitials(colors)}>
                  <User size={14} />
                </span>
              )}
              {!isMobile && isAuthenticated && (
                <ChevronDown
                  size={13}
                  color={colors.secondaryText}
                  style={{
                    transition: "transform 0.2s ease",
                    transform: showDropdown ? "rotate(180deg)" : "rotate(0)",
                  }}
                />
              )}
            </button>
          </div>
        </div>

        {/* Dropdown */}
        {showDropdown && (
          <div
            ref={dropdownRef}
            style={styles.dropdown(colors, isMobile)}
          >
            {isAuthenticated ? (
              <>
                {/* Identity block */}
                <div style={styles.dropdownHeader(colors)}>
                  <span style={styles.dropdownAvatar(colors)}>
                    {initialsOf(user)}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.dropdownName(colors)}>
                      {displayName}
                    </div>
                    <div style={styles.dropdownContact(colors)}>
                      {user?.email || user?.phone_number || "No contact"}
                    </div>
                    {roleMeta && (
                      <span
                        style={{
                          ...styles.rolePill,
                          color: roleTone,
                          background: `${roleTone}18`,
                          borderColor: `${roleTone}33`,
                        }}
                      >
                        <roleMeta.icon size={9} />
                        {roleMeta.label}
                      </span>
                    )}
                  </div>
                </div>

                {/* Nav items */}
                <div style={styles.dropdownGroup}>
                  <DropdownItem
                    colors={colors}
                    icon={User}
                    label="My profile"
                    onClick={() => {
                      setShowDropdown(false);
                      navigate("/profile");
                    }}
                  />
                  <DropdownItem
                    colors={colors}
                    icon={ClipboardList}
                    label="Data orders"
                    onClick={() => {
                      setShowDropdown(false);
                      navigate("/orders");
                    }}
                  />
                  <DropdownItem
                    colors={colors}
                    icon={ShoppingBag}
                    label="Marketplace orders"
                    onClick={() => {
                      setShowDropdown(false);
                      navigate("/marketplace/orders");
                    }}
                  />
                  <DropdownItem
                    colors={colors}
                    icon={CreditCard}
                    label="Wallet"
                    onClick={() => {
                      setShowDropdown(false);
                      navigate("/wallet");
                    }}
                  />
                  <DropdownItem
                    colors={colors}
                    icon={Settings}
                    label="Settings"
                    onClick={() => {
                      setShowDropdown(false);
                      navigate("/settings");
                    }}
                  />
                </div>

                <div style={styles.dropdownDivider(colors)} />

                <div style={styles.dropdownGroup}>
                  <DropdownItem
                    colors={colors}
                    icon={LogOut}
                    label="Sign out"
                    destructive
                    onClick={handleLogout}
                  />
                </div>
              </>
            ) : (
              <div style={{ padding: 14 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowDropdown(false);
                    navigate("/login");
                  }}
                  style={styles.signInBtn(colors)}
                >
                  <User size={15} />
                  Sign in
                </button>
                <div style={styles.newHere(colors)}>
                  New here?{" "}
                  <span
                    onClick={() => {
                      setShowDropdown(false);
                      navigate("/register");
                    }}
                    style={styles.newHereLink(colors)}
                  >
                    Create account
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Command palette */}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />
    </>
  );
};

/* ══════════════════════════════════════════════════════════
   Small sub-components
   ══════════════════════════════════════════════════════════ */

const IconButton = ({ colors, children, ...rest }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      {...rest}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 36,
        height: 36,
        borderRadius: 9,
        border: "none",
        background: hovered ? colors.hoverBg : "transparent",
        color: colors.text,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        position: "relative",
        flexShrink: 0,
        transition: "background 0.15s ease",
      }}
    >
      {children}
    </button>
  );
};

const DropdownItem = ({ colors, icon: Icon, label, onClick, destructive }) => {
  const [hovered, setHovered] = useState(false);
  const fg = destructive ? colors.error : colors.text;
  const bg = hovered
    ? destructive
      ? colors.errorBg
      : colors.hoverBg
    : "transparent";
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        padding: "9px 12px",
        borderRadius: 9,
        border: "none",
        background: bg,
        color: fg,
        cursor: "pointer",
        fontSize: 13,
        fontWeight: 500,
        textAlign: "left",
        transition: "background 0.15s ease",
        fontFamily: "inherit",
      }}
    >
      <Icon size={15} color={destructive ? colors.error : colors.secondaryText} />
      {label}
    </button>
  );
};

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  header: (c) => ({
    position: "sticky",
    top: 0,
    zIndex: 100,
    background: c.glassBg,
    backdropFilter: "blur(16px) saturate(160%)",
    WebkitBackdropFilter: "blur(16px) saturate(160%)",
    borderBottom: `1px solid ${c.border}`,
    flexShrink: 0,
    // Clears the iPhone notch / Dynamic Island / Android status bar.
    // Falls back to 0px on devices without a safe-area inset, so the
    // desktop layout is unaffected.
    paddingTop: "env(safe-area-inset-top, 0px)",
  }),

  rail: (isMobile) => ({
    display: "flex",
    alignItems: "center",
    gap: isMobile ? 6 : 16,
    height: isMobile ? 56 : 62,
    padding: isMobile ? "0 10px" : "0 20px",
    maxWidth: 1600,
    margin: "0 auto",
    width: "100%",
  }),

  leftCluster: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },

  brandRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 2,
    borderRadius: 8,
    flexShrink: 0,
  },
  brandLogo: (isMobile) => ({
    width: isMobile ? 28 : 30,
    height: isMobile ? 28 : 30,
    objectFit: "contain",
    flexShrink: 0,
    display: "block",
  }),
  brandText: (c) => ({
    fontSize: 17,
    fontWeight: 800,
    color: c.heading,
    letterSpacing: -0.3,
    whiteSpace: "nowrap",
  }),

  navRow: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    marginLeft: 8,
    flexShrink: 0,
  },
  navLink: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 11px",
    borderRadius: 8,
    background: "transparent",
    border: "none",
    color: c.secondaryText,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 0.15s ease, color 0.15s ease",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  }),

  rightCluster: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginLeft: "auto",
    flexShrink: 0,
  },

  searchTrigger: (c, isMobile) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    height: 36,
    padding: isMobile ? "0 10px" : "0 10px 0 11px",
    borderRadius: 10,
    border: `1px solid ${c.border}`,
    background: c.subtleBg || c.hoverBg,
    color: c.secondaryText,
    cursor: "pointer",
    fontSize: 12.5,
    fontWeight: 500,
    transition: "border-color 0.15s ease",
    fontFamily: "inherit",
    flexShrink: 0,
  }),
  searchTriggerText: (c) => ({
    color: c.secondaryText,
    marginRight: 4,
  }),
  kbd: (c) => ({
    fontSize: 10,
    color: c.secondaryText,
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    padding: "1px 5px",
    borderRadius: 4,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontWeight: 600,
  }),

  walletPill: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 36,
    padding: "0 12px",
    borderRadius: 10,
    border: `1px solid ${c.border}`,
    background: c.subtleBg || c.hoverBg,
    color: c.text,
    cursor: "pointer",
    fontSize: 12.5,
    fontWeight: 700,
    fontFamily: "inherit",
    flexShrink: 0,
  }),

  cartBadge: (c) => ({
    position: "absolute",
    top: 2,
    right: 2,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    background: c.btnBg,
    color: c.btnText,
    fontSize: 9.5,
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 4px",
    border: `2px solid ${c.raisedBg}`,
    lineHeight: 1,
  }),

  avatarBtn: (c, open) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 36,
    padding: "0 6px 0 4px",
    borderRadius: 999,
    border: `1px solid ${open ? c.border : "transparent"}`,
    background: open ? c.hoverBg : "transparent",
    cursor: "pointer",
    transition: "background 0.15s ease, border-color 0.15s ease",
    fontFamily: "inherit",
    flexShrink: 0,
  }),
  avatarInitials: (c) => ({
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${c.btnBg}, ${c.btnBg}cc)`,
    color: "#fff",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.3,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),

  // ── Dropdown ──────────────────────────────────────────
  dropdown: (c, isMobile) => ({
    position: "absolute",
    top: `calc(100% + 6px)`,
    right: isMobile ? 10 : 20,
    minWidth: 280,
    background: c.raisedBg,
    borderRadius: 14,
    border: `1px solid ${c.border}`,
    boxShadow: `0 18px 48px ${c.shadowStrong || c.shadow}`,
    padding: 6,
    zIndex: 200,
    animation: "dropdownIn 0.15s ease",
  }),
  dropdownHeader: (c) => ({
    display: "flex",
    gap: 12,
    alignItems: "center",
    padding: "14px 14px 12px",
    borderBottom: `1px solid ${c.border}`,
    marginBottom: 4,
  }),
  dropdownAvatar: (c) => ({
    width: 42,
    height: 42,
    borderRadius: 12,
    background: `linear-gradient(135deg, ${c.btnBg}, ${c.btnBg}cc)`,
    color: "#fff",
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: 0.4,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
  dropdownName: (c) => ({
    fontSize: 13.5,
    fontWeight: 700,
    color: c.heading,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  dropdownContact: (c) => ({
    fontSize: 11.5,
    color: c.secondaryText,
    marginTop: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  rolePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    marginTop: 5,
    fontSize: 9.5,
    fontWeight: 800,
    padding: "2px 8px",
    borderRadius: 999,
    border: "1px solid transparent",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  dropdownGroup: {
    padding: 4,
  },
  dropdownDivider: (c) => ({
    height: 1,
    background: c.border,
    margin: "4px 8px",
  }),

  signInBtn: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    width: "100%",
    padding: 11,
    borderRadius: 10,
    border: "none",
    background: c.btnBg,
    color: c.btnText,
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 13.5,
    fontFamily: "inherit",
  }),
  newHere: (c) => ({
    textAlign: "center",
    marginTop: 10,
    fontSize: 12,
    color: c.secondaryText,
  }),
  newHereLink: (c) => ({
    color: c.linkColor,
    cursor: "pointer",
    fontWeight: 700,
  }),
};

export default Header;